"use client";

import { useState } from "react";
import { Frame, Slider, Note } from "./parts";

/**
 * Chiffre d'affaires n'est pas revenu, et ce n'est pas non plus base imposable.
 *
 * Trois grandeurs circulent sous le même mot dans la tête du débutant, et
 * l'écart entre elles se compte en milliers d'euros. La démo les pose côte à
 * côte sur la même échelle, sur un seul chiffre d'affaires.
 *
 * Le piège que l'empilement rend visible : l'abattement forfaitaire ne rembourse
 * rien et ne se cumule pas avec les cotisations. C'est une convention fiscale
 * appliquée au chiffre d'affaires BRUT, pas à ce qui reste après cotisations.
 * Beaucoup de lecteurs croient l'inverse et sous-estiment leur impôt.
 *
 * Taux du référentiel arbitré du 05/09/2026.
 */

const L = {
    fr: {
        title: "Trois chiffres que tout le monde confond",
        activity: "Activité",
        vente: "Vente de marchandises",
        services: "Prestations de services (BIC)",
        bnc: "Activité libérale (BNC)",
        ca: "Chiffre d'affaires encaissé",
        rows: {
            ca: "Chiffre d'affaires",
            caSub: "ce que vos clients vous versent",
            net: "Ce qui reste après cotisations",
            netSub: "votre argent, avant impôt",
            base: "Base imposable",
            baseSub: "ce que le fisc regarde, après abattement",
        },
        warn: "La base imposable est calculée sur le chiffre d'affaires brut, jamais sur ce qui vous reste. Les deux abattements ne s'additionnent pas.",
        note: "Ce calcul ne comprend ni la contribution à la formation professionnelle, ni la taxe de chambre consulaire, ni la CFE, ni l'impôt lui-même. Il montre l'ordre de grandeur et la mécanique, pas votre situation exacte.",
    },
    en: {
        title: "Three figures everyone mixes up",
        activity: "Activity",
        vente: "Sale of goods",
        services: "Commercial services (BIC)",
        bnc: "Liberal profession (BNC)",
        ca: "Turnover received",
        rows: {
            ca: "Turnover",
            caSub: "what your clients pay you",
            net: "Left after contributions",
            netSub: "your money, before tax",
            base: "Taxable base",
            baseSub: "what the tax office looks at, after the allowance",
        },
        warn: "The taxable base is computed on gross turnover, never on what is left to you. The two deductions do not stack.",
        note: "This calculation excludes the training contribution, the chamber levy, the CFE and the tax itself. It shows the order of magnitude and the mechanism, not your exact situation.",
    },
};

const ACTIVITES = {
    vente: { cotis: 0.123, abattement: 0.71 },
    services: { cotis: 0.212, abattement: 0.5 },
    bnc: { cotis: 0.256, abattement: 0.34 },
};

const money = (v, lang) =>
    `${Math.round(v).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")} €`;

export default function MeTurnoverToIncome({ lang = "fr" }) {
    const t = L[lang] || L.fr;
    const [activite, setActivite] = useState("bnc");
    const [ca, setCa] = useState(36000);

    const { cotis, abattement } = ACTIVITES[activite];
    const net = ca * (1 - cotis);
    const base = ca * (1 - abattement);

    const lignes = [
        { cle: "ca", valeur: ca, sub: t.rows.caSub, fort: true },
        { cle: "net", valeur: net, sub: t.rows.netSub },
        { cle: "base", valeur: base, sub: t.rows.baseSub },
    ];

    return (
        <Frame icon="layers-minimalistic-linear" title={t.title}>
            <div className="flex flex-wrap gap-2 mb-5">
                {["vente", "services", "bnc"].map((cle) => (
                    <button
                        key={cle}
                        type="button"
                        onClick={() => setActivite(cle)}
                        className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                            activite === cle
                                ? "bg-primary text-white"
                                : "bg-gray-100 dark:bg-white/[0.06] text-gray-600 dark:text-lightBlue hover:bg-gray-200 dark:hover:bg-white/10"
                        }`}
                    >
                        {t[cle]}
                    </button>
                ))}
            </div>

            <Slider
                label={t.ca}
                value={ca}
                onChange={setCa}
                min={5000}
                max={83600}
                step={1000}
                display={money(ca, lang)}
            />

            <div className="mt-6 space-y-4">
                {lignes.map((l) => (
                    <div key={l.cle}>
                        <div className="flex justify-between items-baseline gap-3 mb-1">
                            <span className="text-sm text-gray-600 dark:text-lightBlue">
                                {t.rows[l.cle]}
                            </span>
                            <span
                                className={`tabular-nums shrink-0 dark:text-white ${l.fort ? "text-lg font-bold" : "text-sm font-bold"}`}
                            >
                                {money(l.valeur, lang)}
                            </span>
                        </div>
                        <div className="h-2.5 rounded-full bg-gray-100 dark:bg-white/[0.06] overflow-hidden">
                            <div
                                className={`h-full transition-all duration-200 ${l.fort ? "bg-primary" : "bg-primary/45"}`}
                                style={{ width: `${(l.valeur / ca) * 100}%` }}
                            />
                        </div>
                        <div className="text-xs text-gray-500 dark:text-lightBlue/70 mt-1">
                            {l.sub}
                        </div>
                    </div>
                ))}
            </div>

            <p className="text-sm text-amber-600 dark:text-amber-400 mb-0 mt-5">
                {t.warn}
            </p>
            <Note>{t.note}</Note>
        </Frame>
    );
}
